import React, { useRef, useEffect, useState } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler'
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils'

interface Point {
  x: number
  y: number
  z: number
}

// Easing function: quadratic ease-in-out.
const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)

// Add this helper function at the top level
const loadGLBAsBuffer = async (url: string): Promise<ArrayBuffer> => {
  try {
    const response = await fetch(url, {
      headers: {
        Accept: 'model/gltf-binary',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to load GLB: ${response.status} ${response.statusText}`)
    }

    const buffer = await response.arrayBuffer()
    return buffer
  } catch (error) {
    console.error('Fetch error:', error)
    throw error
  }
}

const ParticleMorpherCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const numPoints = 12000
  const particleRadius = 0.4

  const [glbFormations, setGlbFormations] = useState<Point[][]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Adjustable scale for formations.
  const objectScaleMultiplier = 4
  // Add these refs to cache values
  const scaleRef = useRef<number>(0)
  const dprRef = useRef<number>(1)
  const rectRef = useRef<DOMRect | null>(null)

  // Load GLB files and sample points.
  useEffect(() => {
    const loadGLBFormations = async () => {
      const glbPaths = ['/models/glb/1.glb', '/models/glb/2.glb', '/models/glb/3.glb']
      const formations: Point[][] = []
      const loader = new GLTFLoader()

      for (const path of glbPaths) {
        try {
          // Use the helper function to load the binary data
          const buffer = await loadGLBAsBuffer(path)

          // Create a Uint8Array view of the buffer to check the magic number
          const header = new Uint8Array(buffer, 0, 4)
          const magic = String.fromCharCode(...header)

          // GLB files should start with 'glTF'
          if (magic !== 'glTF') {
            throw new Error('Invalid GLB file format')
          }

          const gltf = await new Promise<GLTF>((resolve, reject) => {
            try {
              // Pass the buffer directly to parse
              loader.parse(buffer, '', resolve, (error) => {
                console.error('GLTFLoader parse error:', error)
                reject(error)
              })
            } catch (error) {
              console.error('Parse error:', error)
              reject(error)
            }
          })

          const geometries: THREE.BufferGeometry[] = []
          gltf.scene.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
              const mesh = child as THREE.Mesh
              if (mesh.geometry instanceof THREE.BufferGeometry) {
                const geom = mesh.geometry.clone()
                // Remove uv1 attribute for merging compatibility.
                if (geom.attributes.uv1) {
                  geom.deleteAttribute('uv1')
                }
                geometries.push(geom)
              }
            }
          })

          if (geometries.length > 0) {
            const mergedGeometry = mergeGeometries(geometries, false)
            if (!mergedGeometry) {
              console.warn('Geometry merge failed for:', path)
              continue
            }

            // Set up an initial rotation for a nearly top-down view.
            // We rotate about the X-axis nearly 90° (10° off nadir).
            const angleX = Math.PI / 2 - Math.PI / 3 // ~80° from horizontal
            const angleY = 5
            const angleZ = 4
            const euler = new THREE.Euler(angleX, angleY, angleZ, 'XYZ')
            const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(euler)

            const tempMesh = new THREE.Mesh(mergedGeometry)
            const sampler = new MeshSurfaceSampler(tempMesh).build()
            const samplePosition = new THREE.Vector3()
            const points: Point[] = []

            for (let i = 0; i < numPoints; i++) {
              sampler.sample(samplePosition)
              // Apply the initial rotation.
              const rotatedPoint = samplePosition.clone().applyMatrix4(rotationMatrix)
              points.push({
                x: rotatedPoint.x,
                y: rotatedPoint.y,
                z: rotatedPoint.z,
              })
            }

            // Normalize points to roughly [-1, 1].
            const maxX = Math.max(...points.map((p) => Math.abs(p.x)))
            const maxY = Math.max(...points.map((p) => Math.abs(p.y)))
            const maxZ = Math.max(...points.map((p) => Math.abs(p.z)))
            const maxDim = Math.max(maxX, maxY, maxZ)
            const normalizedPoints = points.map((p) => ({
              x: p.x / (maxDim * objectScaleMultiplier),
              y: p.y / (maxDim * objectScaleMultiplier),
              z: p.z / (maxDim * objectScaleMultiplier),
            }))

            formations.push(normalizedPoints)
          } else {
            console.warn('No suitable geometry found in:', path)
          }
        } catch (err) {
          console.error('Error loading GLB file:', path, err)
          // Continue with other models even if one fails
          continue
        }
      }

      setGlbFormations(formations)
      setIsLoading(false)
    }

    loadGLBFormations()
  }, [numPoints, objectScaleMultiplier])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || glbFormations.length === 0) return

    // Update canvas size - moved outside render Adaptive Active Labeling
    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect()
      rectRef.current = rect
      const dpr = window.devicePixelRatio || 1
      dprRef.current = dpr
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.scale(dpr, dpr)
        // Set persistent context properties here
        ctx.shadowBlur = 5
        ctx.shadowColor = '#a08060'
        ctx.fillStyle = '#a08060'
      }
      scaleRef.current = Math.min(canvas.width, canvas.height) * 1.6
    }

    updateCanvasSize()
    window.addEventListener('resize', updateCanvasSize)

    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    // Pre-calculate trig values outside render Adaptive Active Labeling
    const tiltAngle = (10 * Math.PI) / 180
    const cosT = Math.cos(tiltAngle)
    const sinT = Math.sin(tiltAngle)

    let animationFrameId: number
    const globalStartTime = performance.now()

    // Cache these values
    const holdDuration = 3000
    const morphDuration = 2000
    const cycleDuration = holdDuration + morphDuration
    const spinDuration = 8000
    const focalLength = 100

    const render = (time: number) => {
      const totalSpinElapsed = time - globalStartTime
      const spinAngle = (Math.PI / 4) * Math.sin(totalSpinElapsed / spinDuration)
      const cosA = Math.cos(spinAngle)
      const sinA = Math.sin(spinAngle)

      const cycleTime = time - globalStartTime
      const cyclePhase = cycleTime % cycleDuration
      const cycleCount = Math.floor(cycleTime / cycleDuration)
      const currentIndex = cycleCount % glbFormations.length
      const nextIndex = (currentIndex + 1) % glbFormations.length

      const linearProgress =
        cyclePhase < holdDuration ? 0 : (cyclePhase - holdDuration) / morphDuration
      const progress = easeInOutQuad(linearProgress)

      const currentFormation = glbFormations[currentIndex] || []
      const nextFormation = glbFormations[nextIndex] || currentFormation

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity

      // Batch particle drawing
      ctx.beginPath()

      const chaosAmplitude = progress > 0 ? 0.05 * Math.sin(progress * Math.PI) : 0

      currentFormation.forEach((p, i) => {
        let x = p.x
        let y = p.y
        let z = p.z

        // Only interpolate if actually morphing
        if (progress > 0) {
          const q = nextFormation[i] || p
          x = p.x + (q.x - p.x) * progress
          y = p.y + (q.y - p.y) * progress
          z = p.z + (q.z - p.z) * progress

          if (chaosAmplitude > 0) {
            x += chaosAmplitude * (Math.random() * 2 - 1)
            y += chaosAmplitude * (Math.random() * 2 - 1)
            z += chaosAmplitude * (Math.random() * 2 - 1)
          }
        }

        // Apply rotations
        const x1 = x * cosA - z * sinA
        const z1 = x * sinA + z * cosA
        const z2 = z1 * cosT - y * sinT
        const y2 = z1 * sinT + y * cosT

        const perspective = focalLength / (focalLength + y2)
        const cx = canvas.width / 2 + x1 * perspective * scaleRef.current
        const cy = canvas.height / 2 - z2 * perspective * scaleRef.current

        minX = Math.min(minX, cx)
        minY = Math.min(minY, cy)
        maxX = Math.max(maxX, cx)
        maxY = Math.max(maxY, cy)

        ctx.moveTo(cx + particleRadius, cy)
        ctx.arc(cx, cy, particleRadius, 0, Math.PI * 2)
      })

      // Draw all particles at once
      ctx.fill()

      // Draw bounding box
      ctx.globalCompositeOperation = 'source-over'
      ctx.lineWidth = 2
      ctx.strokeStyle = 'red'
      ctx.strokeRect(minX, minY, maxX - minX, maxY - minY)

      animationFrameId = requestAnimationFrame(render)
    }

    animationFrameId = requestAnimationFrame(render)
    return () => {
      window.removeEventListener('resize', updateCanvasSize)
      cancelAnimationFrame(animationFrameId)
    }
  }, [glbFormations, particleRadius])

  if (isLoading) {
    return <div>Loading models...</div>
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        background: 'transparent',
        display: 'block',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    />
  )
}

export default ParticleMorpherCanvas
